import { useContext, useEffect, createContext, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { SocketContext } from '@context/socket';
import Layout from '@components/layout';
import JackpotStats from '@modules/jackpot/stats';
import GamePanel from '@modules/jackpot/panel';
import WinnerSelector from '@modules/jackpot/winner-selector';
import RecentGames from '@modules/jackpot/recent-games';
import useReduxState from '@hooks/useReduxState';
import styles from './jackpot.module.scss';
import PageEntranceAnimation from '@components/page-entrance-animation';
import { useFetchJackpotLobby } from '@hooks/useFetchJackpotLobby';
import roullete from '@assets/audio/Jackpotwin.mp3';
import { JACKPOT_PLAYER_COLORS as COLORS } from '@consts/jackpot';
import { generateRandomColorsFromUid } from '@utils/jackpot';
import { TGameStatus } from '@api/types/generalGame';
import { IJackpotRoom } from '@api/types/jackpot';

export const playersColorContext = createContext<{
    players: { id: string; color: string; index: number }[];
    colors: string[];
}>({
    players: [],
    colors: [],
});

export const LOW_ROOM_INDEX = 1;
export const HIGH_ROOM_INDEX = 0;
export type TCurrentJackpotRoom =
    | typeof LOW_ROOM_INDEX
    | typeof HIGH_ROOM_INDEX;

export const currentRoomContext = createContext<{
    currentRoom: TCurrentJackpotRoom;
    // eslint-disable-next-line no-unused-vars
    setRoom: (room: TCurrentJackpotRoom) => void;
}>({
    currentRoom: LOW_ROOM_INDEX,
    setRoom: (room) => {
        console.log(room);
    },
});

export const jackpotLobbyStatusContext = createContext<{
    high_room: TGameStatus;
    low_room: TGameStatus;
}>({
    high_room: 'active',
    low_room: 'active',
});

const Jackpot = (): JSX.Element => {
    const [{ rooms }, setJackpot] = useReduxState((state) => state.jackpot);
    const socket: Socket = useContext(SocketContext);

    let audio = new Audio(roullete);

    const fetched: boolean = !!rooms.length;
    const [fetchLobby, updateLobby] = useFetchJackpotLobby();

    const [currentRoom, setCurrentRoom] =
        useState<TCurrentJackpotRoom>(LOW_ROOM_INDEX);
    const [jackpotLobbyStatus] = useState<{
        high_room: TGameStatus;
        low_room: TGameStatus;
    }>({
        high_room: 'active',
        low_room: 'active',
    });

    const randomColors: number[] =
        rooms.length > 0
            ? generateRandomColorsFromUid(rooms[currentRoom].current_game.uid)
            : [1, 2, 3, 4];

    const Views: JSX.Element[] = [
        <div className={styles.wrapper} key={0}>
            <GamePanel
                COLORS={[
                    COLORS[randomColors[0]],
                    COLORS[randomColors[1]],
                    COLORS[randomColors[2]],
                    COLORS[randomColors[3]],
                ]}
                className={styles['section-spacing']}
            />
        </div>,
        <WinnerSelector key={1} />,
        <JackpotStats key={2} />,
        <RecentGames key={3} className={styles['section-spacing']} />,
    ];

    useEffect(() => {
        fetchLobby();

        socket.emit('jackpot');
        socket.on('jackpot-games', ({ rooms }: { rooms: IJackpotRoom[] }) => {
            updateLobby(rooms);
        });
        socket.on('Win-Jackpot', () => {
            if (audio.paused) {
                audio.play();
            }

            fetchLobby();
        });

        return () =>
            setJackpot({
                type: 'RESET_JACKPOT_DATA',
                arg: null,
            });
    }, []);

    let currentColor: number = 0;

    const playersColor: { id: string; color: string; index: number }[] = fetched
        ? rooms[currentRoom].current_game.players.map((player, index) => {
              currentColor++;
              if (currentColor > COLORS.length) currentColor = 0;
              return {
                  id: player.user.id,
                  color:
                      index > COLORS.length - 1
                          ? COLORS[randomColors[currentColor]]
                          : COLORS[randomColors[index]],
                  index,
              };
          })
        : [];

    return (
        <Layout title="jackpot">
            <playersColorContext.Provider
                value={{
                    players: playersColor,
                    colors: COLORS,
                }}
            >
                <currentRoomContext.Provider
                    value={{
                        currentRoom,
                        setRoom: (room: TCurrentJackpotRoom) =>
                            setCurrentRoom(room),
                    }}
                >
                    <jackpotLobbyStatusContext.Provider
                        value={jackpotLobbyStatus}
                    >
                        <PageEntranceAnimation
                            modules={Views}
                            {...{ fetched }}
                        />
                    </jackpotLobbyStatusContext.Provider>
                </currentRoomContext.Provider>
            </playersColorContext.Provider>
        </Layout>
    );
};
export default Jackpot;
